import React, { useEffect, useRef, useState } from 'react'
import { CheckCircle, XCircle, Warning, Info, X } from '@phosphor-icons/react'
import { AUTO_DISMISS_MS, ToastVariant } from '../../utils/toast'

const VARIANT_META = {
  success: { icon: CheckCircle, title: 'Success' },
  error: { icon: XCircle, title: 'Error' },
  warning: { icon: Warning, title: 'Warning' },
  info: { icon: Info, title: 'Info' },
} as const

export type ToastItemData = {
  id: string | number
  variant: ToastVariant
  message: string
  exiting: boolean
}

type ToastProps = {
  item: ToastItemData
  onDismiss: (id: string | number) => void
}

export function Toast({ item, onDismiss }: ToastProps) {
  const meta = VARIANT_META[item.variant]
  const Icon = meta.icon
  const [progress, setProgress] = useState(100)
  const startRef = useRef(Date.now())
  const rafRef = useRef(0)

  useEffect(() => {
    if (item.exiting) return

    const timer = window.setTimeout(() => {
      onDismiss(item.id)
    }, AUTO_DISMISS_MS)

    return () => window.clearTimeout(timer)
  }, [item.id, item.exiting, onDismiss])

  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - startRef.current
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100)
      setProgress(pct)
      if (pct > 0) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  return (
    <div
      className={`aa-toast aa-toast--${item.variant}${
        item.exiting ? ' aa-toast--exiting' : ''
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="aa-toast__body">
        <Icon
          className="aa-toast__icon"
          size={20}
          weight="fill"
          aria-hidden
        />
        <div className="aa-toast__content">
          <p className="aa-toast__title">{meta.title}</p>
          <p className="aa-toast__message">{item.message}</p>
        </div>
        <button
          type="button"
          className="aa-toast__close"
          aria-label="Dismiss notification"
          onClick={() => onDismiss(item.id)}
        >
          <X size={16} weight="regular" aria-hidden />
        </button>
      </div>
      <div className="aa-toast__progress">
        <div
          className="aa-toast__progress-bar"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}
